/* -------------------------------------------------------------------------------------
 * Helper Functions
 * -------------------------------------------------------------------------------------
 */
export const injectDynamicFees = (copyContent: string, feeMap: any, convertToUpToPricing?: boolean) => {
  if (!isCopyDynamic(copyContent)) {
    return;
  }
  if (feeMap) {
    let newContent = copyContent;

    if (newContent.includes("up to $[rsn_fee]")) {
      newContent = newContent.replace("up to $[rsn_fee]", "$[rsn_fee]");
    }

    // if a calculation error, replace content with "up to $[tag]"
    if (convertToUpToPricing === true) {
      const convert = (text: string, tag: string) => {
        if (text?.includes(`$${tag}`)) {
          if (text?.includes(`up to $${tag}`)) {
            return text?.replace(tag, feeMap[tag]);
          }
          return text?.replace(`$${tag}`, `up to $${feeMap[tag]}`)
        }
        return text;
      }
      newContent = convert(newContent, "[rsn_fee]");
      newContent = convert(newContent, "[total_price]");
      newContent = convert(newContent, "[ext_total_price]");
      return newContent;
    }
    newContent = newContent?.replace?.(
      /\[total_price\]|\[rsn_fee\]|\[gemini_amount\]|\[offer_duration\]|\[offer_discount\]|\[ext_total_price]|\[ext_offer_duration\]|\[ext_offer_discount\]/g,
      matched => feeMap[matched] || matched
    ) || "";
    return newContent;
  }
};

export const isCopyDynamic = (copyText: string) => {
  if (!copyText) {
    return false;
  }
  return ['[total_price]', '[rsn_fee]', '[gemini_amount]', '[offer_duration]', '[offer_discount]', '[ext_total_price]', '[ext_offer_duration]', '[ext_offer_discount]']
    .some(tag => copyText?.includes(tag));
};

export const isCopyFetchDependent = (copyText: string) => ['[rsn_fee]', '[total_price]', '[ext_total_price]']
  .some(tag => copyText?.includes(tag));

export const hasMultipleOffers = (copyObj: any[]) => {
  if (!copyObj || !Array.isArray(copyObj)) {
    return false;
  }

  return ['[offer_duration]', '[offer_discount]', '[ext_total_price]', '[ext_offer_duration]', '[ext_offer_discount]']
    .some(tag => copyObj?.some(copy => copy?.content?.includes(tag)));
};

export const isValidPrice = (price: any): boolean => {
  if (isNaN(price) || price === null || price === undefined) {
    return false;
  }
  if (typeof price === "number") {
    return isFinite(price) && price >= 0 && price >= 0.0 && price >= 0.00;
  }
  if (typeof price === "string") {
    // any number of digits period "."
    // optionally a period and exactly two digits after
    const validPrice = /^\d+(\.\d{2})?$/;
    return price !== "" && validPrice.test(price);
  }
  return false;
}

export const packageNameNormalizing = (data: string) => {
  if (!data) {
    return '';
  }
  const normalizedString = data?.normalize("NFD")?.replace(/[\u0300-\u036f]/g, "");
  const cleanedString = normalizedString?.replace(/[^a-zA-Z0-9\s]/g, "");
  return cleanedString ? cleanedString : '';
};

export const isZipcodeDependentCardPrices = (cards: any[], isToggle2Data?: boolean): boolean => {
  try {
    return (
      cards.some(card => !(
        card?.productName?.content?.includes("ENTERTAINMENT") ||
          card?.productName?.content?.includes("MÁS LATINO") ||
          card?.productName?.content?.includes("ÓPTIMO MÁS")
      )) &&
      cards.some(card => {
        if (isToggle2Data) {
          return card?.toggle2PlanCardData?.priceCopy?.some(copy => isCopyFetchDependent(copy?.content)) &&
                 card?.productPrice?.priceCopy?.some(copy => isCopyFetchDependent(copy?.content))
        }
        return card?.productPrice?.priceCopy?.some(copy => isCopyFetchDependent(copy?.content))
      })
    );
  } catch {
    return false;
  }
};

const getOrdinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100; // get last 2 digits of number
  // (v - 20) % 10 handles numbers ending in 11, 12, and 13, which always have "th" suffix.
  return n + (
    s[(v - 20) % 10] || // valid index (0,1,2) in the array, return s[v]
    s[v] || // if > 20
    s[0] // default to "th"
  );
};

export const getMultiOfferDuration = (startMonth: number | null, endMonth: number) => {
  if (startMonth === null && endMonth > 1) {
    return `${endMonth} mos`
  }
  if (startMonth === null && endMonth === 1) {
    return "1st mo"
  } else if (startMonth === endMonth) {
    return `${getOrdinal(startMonth)} mo`;
  } else if (endMonth - startMonth === 1) {
    return `${startMonth}&${endMonth}`
  }
  return `${startMonth}-${endMonth}`
}
/** ------------------------------------------------------------------------------------ */
/**
 * @description Add dynamic fees ([rsn_fee], [total_price]) to copy
 * @params cardData (cardSet), offerPricesData, rsnFeeNum, isCardSetSatellite, isNoLocals, convertToUpToPricing
 * @returns modifiedOfferPrices, modifiedPriceCopyArrs
 */
export function getCardsWithDynamicData({
  cards,
  rsnFeeNum,
  useGeminiFee,
  convertToUpToPricing
}) {
  try {
    const totalPrices = [];
    const moddedCards = [];

    for (const card of cards) {
      const priceCopyObj = card?.productPrice?.priceCopy || [];
      const injectionIsNeeded = priceCopyObj?.some(copy => isCopyDynamic(copy?.content));

      let totalPrice: number;
      let extTotalPrice: number;
      const subtotalPrice = card?.pricingData?.offerSubtotal; // offer + (opt. gemini fee)
      const extSubtotalPrice = card?.pricingData?.extOfferSubtotal; // offer + (opt. gemini fee) + discount
      const useRsnFee = card?.pricingData?.useRsnFee ?? true;
      const rsnFee = card?.pricingData?.rsnFee || rsnFeeNum;

      /* - if no copy, or no copy content has [/] indicators, push content and continue
         * - push moddedContent as original content to simplify loading logic
        */
      if (priceCopyObj?.length === 0 || !injectionIsNeeded) {
        totalPrices.push(subtotalPrice.toFixed(2));
        moddedCards.push({
          ...card,
          productPrice: {
            ...card?.productPrice,
            priceCopy: card?.productPrice?.priceCopy?.map(copy => ({
              ...copy,
              moddedContent: copy?.content
            })),
            totalPriceNum: subtotalPrice.toFixed(2)
          }
        });
        continue;
      }

      /* - Calculate dynamic total prices, inject them, and check for errors */
      if (isValidPrice(subtotalPrice) && isValidPrice(rsnFeeNum)) {
        totalPrice = (subtotalPrice + (useRsnFee ? rsnFeeNum : 0)).toFixed(2);
        totalPrices.push(totalPrice);
      }
      if (!isValidPrice(totalPrice)) {
        throw new Error(`total price calculation failed :( subtotalPrice: ${subtotalPrice} rsnFee: '${rsnFeeNum} totalPrice: ${totalPrice}'`);
      }

      if (hasMultipleOffers(priceCopyObj)) {
        if (isValidPrice(extSubtotalPrice) && isValidPrice(rsnFeeNum)) {
          extTotalPrice = (extSubtotalPrice + rsnFee).toFixed(2);
        }
        if (!isValidPrice(extTotalPrice)) {
          throw new Error(`extended price calculation failed :( subtotalPrice: ${extSubtotalPrice} rsnFee: '${rsnFeeNum} totalPrice: ${extTotalPrice}'`);
        }
      }

      const injectMap: any = {
        ...card?.pricingData?.ssrCopyMap,
        '[total_price]': totalPrice,
        '[ext_total_price]': extTotalPrice,
        '[rsn_fee]': rsnFeeNum.toFixed(2)
      };

      if (priceCopyObj?.length > 0) {
        const moddedCopy = [];
        for (const copyObj of priceCopyObj) {
          moddedCopy.push({
            ...copyObj,
            moddedContent: injectDynamicFees(copyObj?.content, injectMap, convertToUpToPricing)
          });
        }
        moddedCards.push({
          ...card,
          productPrice: {
            ...card?.productPrice,
            priceCopy: moddedCopy,
            totalPriceNum: totalPrice // analytics reference
          }
        });
      }
    }

    return {
      moddedCards,
      totalPrices
    };
  } catch (err) {
    console.error("[PlanCard DynamicPrices] Error in dynamic price calculation", err);
    try {
      if (!convertToUpToPricing) {
        return getCardsWithDynamicData({
          cards,
          rsnFeeNum,
          useGeminiFee,
          convertToUpToPricing: true
        });
      }
      return {
        moddedCards: cards.map(card => ({
          ...card,
          productPrice: {
            ...card?.productPrice,
            priceCopy: []
          }
        })),
        totalPrices: cards.map(() => null)
      };
    } catch (err) {
      console.error("[PlanCard DynamicPrices] FATAL: Error setting fallback prices", err);
      return {
        moddedCards: cards.map(card => ({
          ...card,
          productPrice: {
            ...card?.productPrice,
            priceCopy: []
          }
        })),
        totalPrices: cards.map(() => null)
      };
    }
  }
};
